import {
  AccountingPaymentStatus,
  AccountingSubType,
  AccountingType,
} from "@entities/accounting/lib/constants";
import { BackednResponse } from "@shared/lib/types";
import {
  IBrandAccounting,
  IInvoicingAggregates,
} from "@shared/lib/types/accounting";
import { Popover, Table } from "@shared/ui";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import type { TableProps } from "antd";
import { Button, Dropdown, MenuProps } from "antd";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  FileMinus,
  FilePlus,
  FileText,
  Globe,
  Info,
  Loader2,
  MoreVertical,
  PieChart,
} from "lucide-react";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AddCreditNoteModal } from "./components/AddCreditNoteModal";
import ViewCreditNoteModal from "./components/ViewCreditNoteModal";
import { ViewTransactionModal } from "./components/ViewTransactionModal";
import {
  getDocTypeIconStyleKey,
  getDocTypeLabel,
  getOverdueDays,
  getPaymentStatusLabel,
  getStatusStyleKey,
  isOverdue,
} from "./lib/helpers";
import styles from "./styles.module.scss";

export interface BriefAccountingTableProps {
  invoices: IBrandAccounting[] | undefined;
  isFetching: boolean;
  pagination?: { totalCount?: number; hasNextPage?: boolean };
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  invoicingAggregates?: BackednResponse<IInvoicingAggregates>;
  title?: ReactNode;
}

export function BriefAccountingTable({
  invoices,
  isFetching,
  pagination,
  fetchNextPage,
  hasNextPage,
  invoicingAggregates,
  title
}: BriefAccountingTableProps) {
  const [creditNoteModal, setCreditNoteModal] = useState<{
    isOpen: boolean;
    invoice: IBrandAccounting | null;
  }>({ isOpen: false, invoice: null });
  const [viewTransactionModal, setViewTransactionModal] = useState<{
    isOpen: boolean;
    transaction: IBrandAccounting | null;
  }>({ isOpen: false, transaction: null });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isLoadingRef = useRef(false);

  const handleScroll = useCallback(() => {
    const bodyEl =
      scrollRef.current?.querySelector<HTMLDivElement>(".ant-table-body") ??
      scrollRef.current?.querySelector<HTMLDivElement>(
        ".ant-table-tbody-virtual-holder"
      );
    if (!bodyEl || isFetching || isLoadingRef.current) return;

    const nearBottom =
      bodyEl.scrollTop + bodyEl.clientHeight >= bodyEl.scrollHeight - 120;
    if (nearBottom && hasNextPage && fetchNextPage) {
      isLoadingRef.current = true;
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (!isFetching) {
      isLoadingRef.current = false;
    }
  }, [isFetching]);

  useEffect(() => {
    const body =
      scrollRef.current?.querySelector<HTMLDivElement>(".ant-table-body");
    if (!body) return;
    body.addEventListener("scroll", handleScroll);
    return () => body.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // const totalBase = invoices?.reduce(
  //   (sum, doc) =>
  //     sum +
  //     (doc.type === AccountingType.credit ? -1 : 1) *
  //       Math.abs(doc.baseAmount ?? 0),
  //   0
  // );
  // const totalGst = invoices?.reduce(
  //   (sum, doc) =>
  //     sum +
  //     (doc.type === AccountingType.credit ? -1 : 1) *
  //       Math.abs(doc.gstAmount ?? 0),
  //   0
  // );
  // const totalTotal = invoices?.reduce(
  //   (sum, doc) =>
  //     sum +
  //     (doc.type === AccountingType.credit ? -1 : 1) *
  //       Math.abs(doc.totalAmount ?? 0),
  //   0
  // );

  const formatTotal = (val: number | undefined): string =>
    typeof val === "number" && !Number.isNaN(val) ? val.toLocaleString() : "-";

  const formatDate = (d: Date | string | undefined) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const columns: TableProps<IBrandAccounting>["columns"] = [
    {
      title: "Document #",
      key: "accountingNumber",
      width: 200,
      render: (_: unknown, record: IBrandAccounting) => {
        const iconKey = getDocTypeIconStyleKey(record.subType);
        const brandName = record.brand?.name;
        return (
          <div className={styles.docNumberCell}>
            <div className={`${styles.docIcon} ${styles[iconKey]}`}>
              {record.subType === AccountingSubType.creditNote ? (
                <FileMinus size={16} />
              ) : record.subType === AccountingSubType.debitNote ? (
                <FilePlus size={16} />
              ) : (
                <FileText size={16} />
              )}
            </div>
            <div>
              <span className={styles.docNumber}>
                {record.accountingNumber ?? record._id}
              </span>
              <div className={styles.brandName}>
                <Globe size={10} />
                <TextWithTooltip
                  text={brandName || ""}
                  textClassName={styles.brandName}
                  limit={20}
                />
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Doc Type",
      key: "subType",
      width: 120,
      render: (_: unknown, record: IBrandAccounting) => (
        <span className={styles.docTypeBadge}>
          {getDocTypeLabel(record.subType)}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (_: unknown, record: IBrandAccounting) => (
        <span className={styles.dateText}>{formatDate(record.date)}</span>
      ),
    },
    {
      title: "Due Date",
      key: "overdueDate",
      width: 140,
      render: (_: unknown, record: IBrandAccounting) => {
        const isInv = record.subType === AccountingSubType.invoice;
        const overdue = isOverdue(record);
        const overdueDays = getOverdueDays(record);

        if (!isInv || !record.overdueDate) {
          return <span className={styles.dateTextMuted}>-</span>;
        }
        return (
          <div>
            <span className={styles.dateText}>
              {formatDate(record.overdueDate)}
            </span>
            {overdue && (
              <span className={styles.overdueBadge}>
                <AlertCircle size={10} /> Overdue ({overdueDays}d)
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Type / Campaign",
      key: "campaign",
      width: 200,
      render: (_: unknown, record: IBrandAccounting) => {
        const isCreditNote = record.subType === AccountingSubType.creditNote;
        const isDebitNote = record.subType === AccountingSubType.debitNote;
        const refDoc = record.creditedToBrandAccounting as
          | IBrandAccounting
          | undefined;

        const campaignNames =
          record.briefs?.map((b) => b.name).filter(Boolean) ?? [];

        const getRefData = () => {
          if (refDoc) {
            return (
              <span className={styles.campaignRef}>
                Ref: {refDoc.accountingNumber ?? refDoc._id}
              </span>
            );
          }
          return null;
        };

        if (isCreditNote || isDebitNote) {
          return (
            <div className={styles.campaignCell}>
              <span className={styles.campaignName}>
                {record.briefs?.[0]?.name ?? "N/A"}
              </span>
              {getRefData()}
            </div>
          );
        }

        if (campaignNames.length === 0) {
          return (
            <div className={styles.campaignCell}>
              <span className={styles.campaignName}>-</span>
              {getRefData()}
            </div>
          );
        }

        if (campaignNames.length === 1) {
          return (
            <div className={styles.campaignCell}>
              <span className={styles.campaignName}>{campaignNames[0]}</span>
              {getRefData()}
            </div>
          );
        }

        const popoverContent = (
          <div className={styles.campaignPopover}>
            Includes: {campaignNames.join(", ")}
          </div>
        );

        return (
          <div
            className={`${styles.campaignCell} ${styles.campaignCellMultiple}`}
          >
            <span className={styles.campaignName}>Multiple Campaigns</span>
            <Popover
              content={popoverContent}
              trigger="hover"
              overlayClassName={styles.campaignPopoverOverlay}
            >
              <span className={styles.campaignInfoIcon}>
                <Info size={12} />
              </span>
            </Popover>
            {getRefData()}
          </div>
        );
      },
    },
    {
      title: "Base Amount",
      key: "baseAmount",
      width: 120,
      align: "right",
      render: (_: unknown, record: IBrandAccounting) => {
        const isNegative = record.type === AccountingType.credit;

        return (
          <span
            className={`${styles.amountCell} ${
              isNegative ? styles.amountCellCredit : ""
            }`}
          >
            ₹{record.baseAmount.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "GST",
      key: "gstAmount",
      width: 100,
      align: "right",
      render: (_: unknown, record: IBrandAccounting) => {
        const isNegative = record.type === AccountingType.credit;

        return (
          <span
            className={`${styles.amountCell} ${
              isNegative ? styles.amountCellCredit : ""
            }`}
          >
            ₹{record.gstAmount.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "Total Amount",
      key: "totalAmount",
      width: 130,
      align: "right",
      render: (_: unknown, record: IBrandAccounting) => {
        const isNegative = record.type === AccountingType.credit;

        return (
          <span
            className={`${styles.amountCell} ${styles.amountCellTotal} ${
              isNegative ? styles.amountCellCredit : ""
            }`}
          >
            ₹{record.totalAmount.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: "Payment Status",
      key: "paymentStatus",
      width: 180,
      render: (_: unknown, record: IBrandAccounting) => {
        const isInv = record.subType === AccountingSubType.invoice;
        if (!isInv) {
          return <span className={styles.dateTextMuted}>-</span>;
        }
        const status = record.paymentStatus;
        const overdue = isOverdue(record);
        const styleKey = status
          ? getStatusStyleKey(status, overdue)
          : "default";
        const label = status ? getPaymentStatusLabel(status) : "Pending";

        return (
          <span className={`${styles.statusBadge} ${styles[styleKey]}`}>
            {status === AccountingPaymentStatus.paid && (
              <CheckCircle2 size={12} />
            )}
            {status === AccountingPaymentStatus.pending && <Clock size={12} />}
            {status === AccountingPaymentStatus.partiallyPaid && (
              <PieChart size={12} />
            )}
            {status === AccountingPaymentStatus.cnIssued && (
              <FileMinus size={12} />
            )}
            {status === AccountingPaymentStatus.pending && overdue
              ? "Overdue"
              : label.replace("Payment ", "")}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_: unknown, record: IBrandAccounting) => {
        const isCreditNote = record.subType === AccountingSubType.creditNote;

        const items: MenuProps["items"] = [
          {
            key: "1",
            label: (
              <a
                onClick={() => {
                  setViewTransactionModal({
                    isOpen: true,
                    transaction: record,
                  });
                }}
                title={`View ${getDocTypeLabel(record.subType)}`}
              >
                View Document
              </a>
            ),
            icon: <ArrowUpRight size={14} />,
          },
        ];

        if (record.subType === AccountingSubType.invoice) {
          items.push({
            key: "2",
            label: "Add Credit Note",
            icon: <FileMinus size={14} />,
            onClick: () =>
              setCreditNoteModal({ isOpen: true, invoice: record }),
          });
        }

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button type="text" icon={<MoreVertical size={16} />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div ref={scrollRef}>
      <Table<IBrandAccounting>
        // className={styles.table}
        loading={isFetching && !invoices?.length}
        title={() => title}
        rowKey={(record) => record._id}
        columns={columns}
        dataSource={invoices ?? []}
        pagination={false}
        scroll={{ y: 600, x: 1200 }}
        showHeader={Boolean(invoices?.length)}
        locale={{
          emptyText: (
            <div className={styles.emptyText}>
              No documents found matching your criteria.
            </div>
          ),
        }}
        summary={() =>
          invoices && invoices.length > 0 ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={5} align="right">
                  <span className={styles.footerTotal}>Total</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span className={styles.footerTotal}>
                    ₹{formatTotal(invoicingAggregates?.data?.baseAmount)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span className={styles.footerTotal}>
                    ₹{formatTotal(invoicingAggregates?.data?.gstAmount)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span
                    className={`${styles.footerTotal} ${styles.footerTotalAmount}`}
                  >
                    ₹{formatTotal(invoicingAggregates?.data?.totalAmount)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} colSpan={2} />
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />

      {isFetching && invoices && invoices.length > 0 && (
        <div className={styles.loader}>
          <Loader2 size={16} className={styles.loaderIcon} />
          Loading more...
        </div>
      )}
      {creditNoteModal.isOpen ? (
        <AddCreditNoteModal
          isOpen={creditNoteModal.isOpen}
          onClose={() => setCreditNoteModal({ isOpen: false, invoice: null })}
          invoice={creditNoteModal.invoice}
        />
      ) : null}
      {(viewTransactionModal.isOpen &&
        viewTransactionModal?.transaction?.subType ===
          AccountingSubType.invoice) ||
      !viewTransactionModal?.transaction?.creditedToBrandAccountingId ? (
        <ViewTransactionModal
          isOpen={viewTransactionModal.isOpen}
          onClose={() =>
            setViewTransactionModal({ isOpen: false, transaction: null })
          }
          transaction={viewTransactionModal.transaction}
        />
      ) : (
        <ViewCreditNoteModal
          isOpen={viewTransactionModal.isOpen}
          onClose={() =>
            setViewTransactionModal({ isOpen: false, transaction: null })
          }
          transaction={viewTransactionModal.transaction}
        />
      )}
    </div>
  );
}
